<template>
	<template v-if="visible">
		<div v-if="slotsCount > 0" class="stats" ref="container" :style="[gridStyle, style]" :class="clazz">
			<stats-cell v-for="s in slots" :cell="s" @action="doAction"></stats-cell>
		</div>
		<div v-else-if="stats.noSlotsMessage" class="formHelp"><sym-icon name="info-16"></sym-icon> {{ stats.noSlotsMessage }}</div>
	</template>
</template>

<script setup lang="ts">
import usePart, {getter, type PartProps, partProps} from "../../Part";
import {computed, ref, type CSSProperties} from "vue";
import {Stats, StatsSlot} from "@t/fd/fixed/Stats.ts";
import {observeWidth} from "@/utils/Container.ts";
import {findGrid, GridResult, PERC_FORMAT, SlotContent, VALUE_FORMAT} from "@fd/editors/charts/StatsSupport.ts";
import StatsCell from "@fd/editors/charts/StatsCell.vue";
import {endpointUrl} from "@fd/containers/AddButton.ts";
import {connectEventBus, injectEnv} from "@fd/Environment.ts";
import {go} from "@/router/Router.ts";
import SymIcon from "@fd/helpers/SymIcon.vue";

const uprops = defineProps({...partProps});
const props = uprops as PartProps;
const stats = computed<Stats>(() => props.part as Stats);
const bus = connectEventBus();
const env = injectEnv();

const {visible, localData, formData, clazz, style} = usePart(props);

const container = ref<HTMLDivElement|null>(null);
const containerWidth = observeWidth(container);

const slotsCount = computed<number>(() => {
	if (!Array.isArray(stats.value.slots)) return 1;	// one error slot
	return slotContents.value.s.length;
});

function meaningful(s:StatsSlot, numerator:number, denominator:number):boolean {
	switch (s.hide) {
		case "IF_ZERO": return numerator !== 0;
		case "IF_ONE": return numerator !== denominator;
		case "IF_NAN": return denominator !== 0;
		case "IF_UNUSEFUL": return numerator !== 0 && denominator !== 0 && numerator !== denominator;
		default: return true;
	}
}

const slotContents = computed<{s:SlotContent[], p:number[]}>(() => {
	if (!Array.isArray(stats.value.slots)) return { s:[{ label: "Missing slots", perc: "!", count: "?", span: 1 }], p:[1]};
	const s:SlotContent[] = [];
	const priorities:number[] = [];
	const w = containerWidth.value;

	// Prepare content
	for (let ss of stats.value.slots) {
		let numerator = 0;
		let denominator = 1;
		let base = false;
		let percFormatted = undefined;
		let countFormatted = undefined;
		let countOfFormatted = undefined;

		if (ss.numerator != null) {
			let v = getter(localData.value, ss.numerator, formData);
			if (typeof v === "number" && !Number.isNaN(v)) numerator = v;

			if (ss.denominator != null) {
				if (ss.denominator === ss.numerator) {
					base = true;
					denominator = numerator;
				} else {
					let v = getter(localData.value, ss.denominator, formData);
					if (typeof v === "number" && !Number.isNaN(v) && v != 0) denominator = v;
				}
				if (denominator !== 0) {
					let percent = numerator / denominator;
					percFormatted = PERC_FORMAT.format(percent);
				} else {
					percFormatted = "-";
				}
				countFormatted = VALUE_FORMAT.format(numerator * (ss.negative ? -1 : 1));
				if (!base) countOfFormatted = VALUE_FORMAT.format(denominator)
			} else {
				// Count only
				percFormatted = VALUE_FORMAT.format(numerator * (ss.negative ? -1 : 1));
			}
		}
		if (meaningful(ss, numerator, denominator)) {
			s.push({
				label: ss.label,
				help: ss.help,
				base: base,
				perc: percFormatted,
				percStyle: ss.color ? { color: ss.color } : undefined,
				count: countFormatted,
				countOf: countOfFormatted,
				span: 1,
				action: ss.action,
				actionArgs: ss.actionArgs,
				href: ss.href,
				hrefParams: ss.hrefParams
			});
			priorities.push(ss.spanRatio || 1);
		}
	}
	return {s, p: priorities};
});

const slots = computed<SlotContent[]>(() => {
	const sc = slotContents.value;
	const priorities = sc.p;
	const s = sc.s;
	const g = grid.value;
	const needFill = (g.C * g.R) - s.length;
	for (let i = 0; i < needFill; i++) {
		// Find index of the slot to be extended
		let maxedIndexes:number[];
		let priorityMax = -1000000;
		for (let j = 0; j < priorities.length; j++) {
			const p = priorities[j];
			if (p > priorityMax) {
				maxedIndexes = [j];
				priorityMax = p;
			} else if (p === priorityMax) maxedIndexes.push(j);
		}
		// maxedIndexes now contain candidates to be spanned more. Let's pick the one with the longest label
		let finalIndex = 0;
		let maxLength = -1;
		for (let j = 0; j < maxedIndexes.length; j++) {
			const sc:SlotContent = s[maxedIndexes[j]];
			const len = sc.label.length;
			if (len > maxLength) {
				finalIndex = maxedIndexes[j];
			}
		}
		// Increase slot content at index finalIndex
		s[finalIndex].span++;
		priorities[finalIndex] -= 1;
	}
	return s;
});

const grid = computed<GridResult>(() => {
	let minW:number = stats.value.minWidth || 128;
	let maxW:number = stats.value.maxWidth || (minW*2);
	return findGrid(slotsCount.value, containerWidth.value, minW, maxW);
});

const gridStyle = computed<CSSProperties>(() => {
	let cols:string[] = [];
	let colCount = grid.value.C;
	for (let c = 0; c < colCount; c++) cols.push("1fr");
	return {
		gridTemplateColumns: cols.join(" ")
	}
});


async function doAction(cell: SlotContent) {
	if (cell.action) {
		let args:any[];
		if (cell.actionArgs) args = cell.actionArgs;
		else args = [];
		await bus.executeCommand(cell.action, ...args);
	} else if (cell.href) {
		const href = endpointUrl(env, cell.href, localData.value, formData, cell.hrefParams);
		go(href);
	}
}

</script>

<style>
</style>